import { Table } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ContentLoader, ContentNoData } from "@components";
import {
  selectCurrentApplicationId,
  selectAppRisksListLoading,
  selectAppRisksList,
  selectAppRiskTotalScore,
  ApplicationsActions,
  selectCurrentApplicationState
} from "@redux";
import { i18, trb } from "@utilities";
import { APP_STATES } from "@config/Applications";
import { EditRiskModal } from "../Modals/EditRiskModal";

import "./RisksTab.less";

const getFormatedTableGroupData = groupKeys => groupKeys.map(name => ({ name }));

const RISK_EDITABLE_STATES = [
  APP_STATES.WAITING_FOR_OFFICER_APPROVAL,
  APP_STATES.ONBOARDING_COMPLETE
];

export const RisksTab = React.memo(() => {
  const dispatch = useDispatch();
  const appId = useSelector(selectCurrentApplicationId);
  const appRisks = useSelector(selectAppRisksList);
  const isLoading = useSelector(selectAppRisksListLoading);
  const risksTotalScore = useSelector(selectAppRiskTotalScore);
  const state = useSelector(selectCurrentApplicationState);

  const [expandedRisksGroups, setExpandedRisksGroups] = useState([]);
  const [selectedRiskToEdit, setSelectedRiskToEdit] = useState();

  const mountedOnce = useRef(false);

  const isEditable = RISK_EDITABLE_STATES.includes(state);

  const appRisksKeys = Object.keys(appRisks);

  useEffect(() => {
    if (mountedOnce.current) return;
    (async () => {
      await dispatch(ApplicationsActions.getClientRisksList(appId));
      mountedOnce.current = true;
    })();
  }, []);

  const toggleRiskGroupRow = (isExpanded, groupName) => {
    if (!isExpanded) {
      setExpandedRisksGroups(expandedRisksGroups.filter(group => group !== groupName));
      return;
    }

    setExpandedRisksGroups([...expandedRisksGroups, groupName]);
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (!appRisksKeys.length) {
    return <ContentNoData />;
  }

  const columns = [
    {
      render: item => trb(i18.ApplicationsLabels?.[item]) || item,
      dataIndex: "name",
      key: "name"
    }
  ];

  const expandedRiskGroupRow = [
    {
      dataIndex: "riskDescription",
      key: "riskDescription",
      title: trb(i18.ApplicationsLabels.RiskDescription)
    },
    {
      dataIndex: "riskScore",
      key: "riskScore",
      title: trb(i18.ApplicationsLabels.RiskScore)
    },
    {
      dataIndex: "comment",
      key: "comment",
      title: trb(i18.ApplicationsLabels.Comment)
    }
  ];

  const handleSelectRiskEdit = risk => {
    if (!isEditable) return;
    setSelectedRiskToEdit(risk);
  };

  return (
    <>
      <div className="risks-tab-container">
        {(risksTotalScore !== null || risksTotalScore !== undefined) && (
          <div className="eb_fs_14 mb-3">
            <span className="eb_fs_500">{trb(i18.ApplicationsLabels.TotalScore)}: </span>
            <span>{risksTotalScore}</span>
          </div>
        )}
        <Table
          columns={columns}
          scroll={{ x: "60vw" }}
          pagination={false}
          expandable={{
            expandedRowRender: record => (
              <Table
                dataSource={appRisks[record.name]}
                columns={expandedRiskGroupRow}
                pagination={false}
                rowClassName={isEditable ? "pointer-cursor" : ""}
                onRow={risk => ({ onClick: () => handleSelectRiskEdit(risk) })}
              />
            ),
            rowExpandable: () => true,
            expandedRowKeys: expandedRisksGroups,
            onExpand: (isExpanded, group) => toggleRiskGroupRow(isExpanded, group.name)
          }}
          dataSource={getFormatedTableGroupData(appRisksKeys)}
          rowKey="name"
        />
      </div>
      {!!selectedRiskToEdit && (
        <EditRiskModal risk={selectedRiskToEdit} onClose={() => setSelectedRiskToEdit(undefined)} />
      )}
    </>
  );
});
